/**
 * Conversas com a DiagIA — auditoria.
 *
 * Lista à esquerda, transcrição à direita. O que se procura aqui é **o que o
 * cliente perguntou e o que a IA respondeu**: quando chega reclamação, quando se
 * quer saber o que o pessoal anda perguntando, quando é preciso mostrar a
 * conversa inteira para alguém.
 *
 * **Ela abre já filtrada, e diz que filtrou.** Nove em cada dez conversas do
 * banco têm só a saudação da IA — o cliente abriu o painel, o veículo entrou no
 * contexto e ninguém digitou nada. Em 12/09/2026 foram 513 de 568. Mostrar as 568
 * esconderia as 55 que interessam, então o padrão é "só com pergunta" e o
 * contador mostra o denominador inteiro.
 *
 * **O texto é mostrado como texto, nunca como HTML.** A IA grava `<strong>` e
 * markdown dentro da mensagem, e o usuário grava o que quiser. Renderizar isso
 * como HTML numa tela interna seria injetar na própria auditoria o que se está
 * auditando.
 *
 * As tags são **apagadas** na leitura — `<strong>Honda</strong>` vira `Honda` —
 * porque são formatação que a IA emitiu, não conteúdo, e com elas cada frase
 * ganha três símbolos que não dizem nada. **A exportação leva o texto bruto**,
 * inteiro, do jeito que está gravado: quando a pergunta for "o que exatamente
 * estava no banco", é a planilha que responde.
 */

import { useEffect, useMemo, useState } from "react"
import {
  exportarConversas, usarConversa, usarConversasDaIA,
  type ConversaDaIA as Conversa,
} from "@/api/conversasIA"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { Aviso, Botao, Campo, estiloCampo, mensagem } from "../outros/pecas"

const POR_PAGINA = 50

export function ConversasDaIA() {
  const hoje = new Date()
  const trintaDias = new Date()
  trintaDias.setDate(hoje.getDate() - 29)

  const [de, setDe] = useState(iso(trintaDias))
  const [ate, setAte] = useState(iso(hoje))
  const [clienteId, setClienteId] = useState(0)
  const [somenteComPergunta, setSomenteComPergunta] = useState(true)
  const [pagina, setPagina] = useState(1)

  // O filtro aplicado, separado do que está nos campos: a consulta custa
  // segundos, e refazê-la a cada tecla na data seria castigo.
  const [pedido, setPedido] = useState<{
    dataInicio: string; dataFim: string; clienteId: number
    somenteComPergunta: boolean; pagina: number
  } | null>(null)

  const [aberta, setAberta] = useState(0)
  const [recado, setRecado] = useState<string | null>(null)
  const [exportando, setExportando] = useState(false)

  const consulta = usarConversasDaIA(
    {
      dataInicio: pedido?.dataInicio ?? de,
      dataFim: pedido?.dataFim ?? ate,
      clienteId: pedido?.clienteId || undefined,
      somenteComPergunta: pedido?.somenteComPergunta ?? true,
      pagina: pedido?.pagina ?? 1,
      porPagina: POR_PAGINA,
    },
    pedido != null,
  )

  const d = consulta.data

  // Trocar de página não deve fechar a transcrição aberta por acidente, mas
  // trocar de recorte sim: a conversa aberta pode não estar mais na lista.
  useEffect(() => { setAberta(0) }, [pedido?.dataInicio, pedido?.dataFim, pedido?.clienteId, pedido?.somenteComPergunta])

  function rodar(p = 1) {
    setRecado(null)
    setPagina(p)
    setPedido({ dataInicio: de, dataFim: ate, clienteId, somenteComPergunta, pagina: p })
  }

  const dias = diasEntre(de, ate)
  const excedeu = dias > 366

  const paginas = d ? Math.max(1, Math.ceil(d.total / d.porPagina)) : 1
  const escondidas = d ? d.totalDeConversasNoPeriodo - d.total : 0

  async function exportar() {
    if (!pedido) return
    setRecado(null)
    setExportando(true)
    try {
      const r = await exportarConversas({
        dataInicio: pedido.dataInicio,
        dataFim: pedido.dataFim,
        clienteId: pedido.clienteId || undefined,
        somenteComPergunta: pedido.somenteComPergunta,
      })
      setRecado(`${r.nome} — ${(r.bytes / 1024 / 1024).toFixed(1)} MB.`)
    } catch (e) {
      setRecado(mensagem(e))
    } finally {
      setExportando(false)
    }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Conversas com a DiagIA"
        detalhe={d
          ? `${d.total.toLocaleString("pt-BR")} conversa(s) · ${d.mensagens.toLocaleString("pt-BR")} mensagens`
          : "O que os clientes perguntaram, e o que a IA respondeu"}
      >
        <Botao
          aoClicar={exportar}
          desligado={!pedido || !d || d.total === 0}
          ocupado={exportando}
          titulo="Uma linha por mensagem, com o recorte inteiro — não só esta página."
        >
          {exportando ? "Montando a planilha…" : "Exportar tudo"}
        </Botao>
        <Botao destaque aoClicar={() => rodar(1)} desligado={excedeu} ocupado={consulta.isFetching}>
          {consulta.isFetching ? "Buscando…" : "Buscar"}
        </Botao>
      </Faixa>

      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)", background: "var(--surface)",
      }}>
        <Campo rotulo="De">
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)}
                 style={{ ...estiloCampo, width: 145 }} />
        </Campo>
        <Campo rotulo="Até" ajuda={`${dias} dia(s)${excedeu ? " — o teto é 366" : ""}`}>
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)}
                 style={{ ...estiloCampo, width: 145 }} />
        </Campo>

        <div style={{ display: "flex", gap: 4, paddingBottom: 2 }}>
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => {
                const f = new Date()
                const i = new Date()
                i.setDate(f.getDate() - (n - 1))
                setDe(iso(i)); setAte(iso(f))
              }}
              style={{
                border: "1px solid var(--line)", borderRadius: 5, padding: "4px 8px",
                fontSize: 11, cursor: "pointer", background: "var(--surface)", color: "var(--txt2)",
              }}
            >
              {n} dias
            </button>
          ))}
        </div>

        <Campo
          rotulo="Cliente"
          ajuda={d ? `${d.clientes.length} conversaram no período` : "aparece depois da primeira busca"}
        >
          <select
            value={clienteId}
            onChange={(e) => setClienteId(Number(e.target.value))}
            style={{ ...estiloCampo, width: 280 }}
          >
            <option value={0}>todos</option>
            {(d?.clientes ?? []).map((c) => (
              <option key={c.clienteId} value={c.clienteId}>
                {c.clienteNome || `#${c.clienteId}`} ({c.conversas})
              </option>
            ))}
          </select>
        </Campo>

        <label style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 11.5,
          color: "var(--txt2)", paddingBottom: 4,
        }}>
          <input
            type="checkbox"
            checked={somenteComPergunta}
            onChange={(e) => setSomenteComPergunta(e.target.checked)}
          />
          Só as que têm pergunta
        </label>
      </div>

      {recado && (
        <div style={{ padding: "8px 18px 0" }}>
          <Aviso>{recado}</Aviso>
        </div>
      )}

      {escondidas > 0 && somenteComPergunta && (
        <div style={{ padding: "8px 18px 0" }}>
          <Aviso>
            {escondidas.toLocaleString("pt-BR")} das {d!.totalDeConversasNoPeriodo.toLocaleString("pt-BR")} conversas
            do período <b>não têm pergunta nenhuma</b> — só a saudação da IA, de quando o cliente
            abriu o painel e não digitou. Estão fora da lista e da exportação; desmarque a caixa
            acima para vê-las.
          </Aviso>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{
          flex: "1 1 auto", minWidth: 0, overflow: "auto",
          borderRight: aberta ? "1px solid var(--line2)" : undefined,
        }}>
          {!pedido ? (
            <p style={{ padding: 18, fontSize: 12.5, color: "var(--txt3)", maxWidth: "62ch", lineHeight: 1.6 }}>
              Escolha o período e mande buscar. A consulta varre a tabela de conversas — 30 dias
              são cerca de 40 mil conversas e levam ~7 segundos —, então ela não roda sozinha ao
              abrir.
            </p>
          ) : consulta.isPending ? (
            <p style={{ padding: 18, fontSize: 12.5, color: "var(--txt3)" }}>Buscando…</p>
          ) : consulta.error ? (
            <p style={{ padding: 18, fontSize: 12.5, color: "var(--erro)" }}>{mensagem(consulta.error)}</p>
          ) : !d || d.itens.length === 0 ? (
            <p style={{ padding: 18, fontSize: 12.5, color: "var(--txt3)" }}>
              Nenhuma conversa no recorte.
            </p>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                <thead>
                  <tr>
                    {["Quando", "Cliente", "Veículo", "Primeira pergunta", "Msg", "Perg."].map((h, i) => (
                      <th key={h} style={{
                        position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
                        textAlign: i >= 4 ? "right" : "left", padding: "6px 10px",
                        fontSize: 10.5, fontWeight: 600, color: "var(--txt2)",
                        borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.itens.map((c) => (
                    <Linha
                      key={c.conversaId}
                      c={c}
                      ativa={aberta === c.conversaId}
                      aoAbrir={() => setAberta(aberta === c.conversaId ? 0 : c.conversaId)}
                    />
                  ))}
                </tbody>
              </table>

              {paginas > 1 && (
                <div style={{
                  display: "flex", gap: 8, alignItems: "center", justifyContent: "center",
                  padding: 12, fontSize: 11.5, color: "var(--txt2)",
                }}>
                  <Botao aoClicar={() => rodar(pagina - 1)} desligado={pagina <= 1}>anterior</Botao>
                  <span>página {pagina} de {paginas.toLocaleString("pt-BR")}</span>
                  <Botao aoClicar={() => rodar(pagina + 1)} desligado={pagina >= paginas}>próxima</Botao>
                </div>
              )}
            </>
          )}
        </div>

        {aberta > 0 && <Transcricao conversaId={aberta} aoFechar={() => setAberta(0)} />}
      </div>

      <BarraDeEstado
        direita={d ? `${d.totalDeConversasNoPeriodo.toLocaleString("pt-BR")} conversas no período` : undefined}
      >
        {d
          ? `${d.clientes.length} cliente(s) conversaram · a exportação leva o recorte inteiro, não a página`
          : "Auditoria: o que foi perguntado e o que foi respondido."}
      </BarraDeEstado>
    </div>
  )
}

function Linha({ c, ativa, aoAbrir }: { c: Conversa; ativa: boolean; aoAbrir: () => void }) {
  return (
    <tr
      onClick={aoAbrir}
      style={{
        cursor: "pointer", borderBottom: "1px solid var(--line2)",
        background: ativa ? "var(--surface2)" : undefined,
      }}
    >
      <td style={{ ...celula, whiteSpace: "nowrap" }}>{quando(c.data)}</td>
      <td style={{ ...celula, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={`${c.clienteNome} · id ${c.clienteId}`}>
        {c.clienteNome || `#${c.clienteId}`}
      </td>
      <td style={{ ...celula, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={c.veiculo}>
        {c.veiculo || "—"}
      </td>
      <td style={{ ...celula, maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={c.primeiraPergunta}>
        {c.primeiraPergunta || <span style={{ color: "var(--txt3)" }}>— sem pergunta</span>}
        {c.erros > 0 && (
          <span style={{ marginLeft: 6, color: "var(--erro)", fontSize: 10.5 }}>
            · {c.erros} erro(s)
          </span>
        )}
      </td>
      <td style={{ ...celula, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.mensagens}</td>
      <td style={{ ...celula, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.perguntas}</td>
    </tr>
  )
}

function Transcricao({ conversaId, aoFechar }: { conversaId: number; aoFechar: () => void }) {
  const conversa = usarConversa(conversaId)
  const c = conversa.data

  const contexto = useMemo(() => {
    if (!c) return []
    return Object.entries(c.assuntosSistemas ?? {})
  }, [c])

  return (
    <aside style={{
      flex: "0 0 480px", width: 480, minWidth: 0, overflowY: "auto",
      background: "var(--surface)",
    }}>
      <div style={{
        position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
        display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
        borderBottom: "1px solid var(--line2)",
      }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>#{conversaId}</span>
        {c && <span style={{ fontSize: 11, color: "var(--txt2)" }}>{quando(c.data)}</span>}
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>
          ×
        </button>
      </div>

      <div style={{ padding: 14 }}>
        {conversa.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Abrindo…</p>
        ) : conversa.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(conversa.error)}</p>
        ) : !c ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Conversa não encontrada.</p>
        ) : (
          <>
            <p style={{ margin: "0 0 4px", fontSize: 12.5, fontWeight: 600 }}>
              {c.veiculoCompleto || [c.marcaNome, c.veiculoModelo, c.motorNome].filter(Boolean).join(" ") || "—"}
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 11, color: "var(--txt2)" }}>
              cliente {c.clienteId}
              {c.assuntoNome && <> · {c.assuntoNome}</>}
              {c.sistemaNome && <> · {c.sistemaNome}</>}
            </p>

            {c.mensagens.length === 0 ? (
              <p style={{ fontSize: 11.5, color: "var(--txt3)", lineHeight: 1.5 }}>
                <b>Nenhuma mensagem gravada.</b> A conversa existe — o veículo entrou no contexto —,
                mas ninguém disse nada.
              </p>
            ) : (
              c.mensagens.map((m) => <Balao key={m.id} m={m} />)
            )}

            <p style={{ margin: "10px 0 0", fontSize: 10, color: "var(--txt3)", lineHeight: 1.5 }}>
              As tags de formatação foram apagadas para leitura. A exportação leva o texto bruto.
            </p>

            {contexto.length > 0 && (
              <details style={{ marginTop: 14 }}>
                <summary style={{ fontSize: 11, color: "var(--txt3)", cursor: "pointer" }}>
                  O que a IA tinha em contexto ({contexto.length} assunto(s))
                </summary>
                <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--txt2)", lineHeight: 1.55 }}>
                  {contexto.map(([assunto, sistemas]) => (
                    <div key={assunto} style={{ marginBottom: 3 }}>
                      <b>{assunto}</b>: {sistemas.join(", ")}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>
    </aside>
  )
}

function Balao({ m }: { m: { remetente: string; texto: string; data: string; feedback: number | null } }) {
  const doUsuario = (m.remetente ?? "").trim().toLowerCase() === "usuario"

  return (
    <div style={{
      marginBottom: 8, padding: "8px 10px", borderRadius: 7,
      border: "1px solid var(--line2)",
      background: doUsuario ? "var(--surface2)" : "transparent",
    }}>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4,
        fontSize: 10, color: "var(--txt3)",
      }}>
        <b style={{ fontSize: 10.5, color: doUsuario ? "var(--acao)" : "var(--txt2)" }}>
          {doUsuario ? "Usuário" : "DiagIA"}
        </b>
        <span>{quando(m.data)}</span>
        {m.feedback != null && (
          <span style={{ marginLeft: "auto" }}>
            feedback {m.feedback > 0 ? "👍" : "👎"}
          </span>
        )}
      </div>
      {/* Como TEXTO, nunca como HTML: a mensagem é exatamente o conteúdo que se
          está auditando, e interpretá-la seria injetá-la na própria auditoria.
          As tags saem porque são formatação da IA; o markdown fica, porque já é
          legível e é o que a IA de fato escreveu. */}
      <div style={{ fontSize: 11.5, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {semTags(m.texto)}
      </div>
    </div>
  )
}

/**
 * Tira as tags HTML preservando as quebras de linha.
 *
 * Não é sanitização — o texto nunca é interpretado como HTML de qualquer forma —,
 * é legibilidade: a saudação da IA vem com `<strong>` em volta do veículo e `<br>`
 * no lugar da quebra, e sem isto cada conversa começa com uma linha de símbolos.
 */
function semTags(texto: string): string {
  return (texto ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
}

const celula: React.CSSProperties = { padding: "5px 10px" }

function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function diasEntre(de: string, ate: string) {
  const a = new Date(de + "T00:00:00")
  const b = new Date(ate + "T00:00:00")
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0
  return Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1
}

function quando(iso8601: string) {
  const d = new Date(iso8601)
  if (Number.isNaN(d.getTime())) return iso8601
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
  })
}
